import * as i0 from '@angular/core';
import { EventEmitter, Component, HostBinding, Input, Output, ViewChild, HostListener, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import * as i1 from 'carbon-components-angular/i18n';
import { I18nModule } from 'carbon-components-angular/i18n';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from 'carbon-components-angular/icon';
import { IconModule } from 'carbon-components-angular/icon';

/**
 * Get started with importing the module:
 *
 * ```typescript
 * import { SearchModule } from 'carbon-components-angular';
 * ```
 *
 * [See demo](../../?path=/story/components-search--basic)
 */
const _c0 = ["input"];
function Search_div_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "div", 5);
  }
}
function Search_ng_template_4_button_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r8 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 10);
    i0.ɵɵlistener("click", function Search_ng_template_4_button_2_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r8);
      const ctx_r7 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r7.openSearch());
    });
    i0.ɵɵnamespaceSVG();
    i0.ɵɵelement(1, "svg", 11);
    i0.ɵɵelementEnd();
  }
}
function Search_ng_template_4__svg_svg_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵnamespaceSVG();
    i0.ɵɵelement(0, "svg", 11);
  }
}
function Search_ng_template_4_Template(rf, ctx) {
  if (rf & 1) {
    const _r10 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "input", 6, 7);
    i0.ɵɵlistener("input", function Search_ng_template_4_Template_input_input_0_listener($event) {
      i0.ɵɵrestoreView(_r10);
      const ctx_r9 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r9.onSearch($event.target.value));
    })("keyup.enter", function Search_ng_template_4_Template_input_keyup_enter_0_listener() {
      i0.ɵɵrestoreView(_r10);
      const ctx_r11 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r11.onEnter());
    });
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(2, Search_ng_template_4_button_2_Template, 2, 0, "button", 8)(3, Search_ng_template_4__svg_svg_3_Template, 1, 0, "svg", 9);
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵproperty("type", ctx_r1.tableSearch || !ctx_r1.toolbar ? "text" : "search")("id", ctx_r1.id)("value", ctx_r1.value)("autocomplete", ctx_r1.autocomplete)("placeholder", ctx_r1.placeholder)("disabled", ctx_r1.disabled)("required", ctx_r1.required);
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !ctx_r1.tableSearch && ctx_r1.toolbar);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngIf", ctx_r1.tableSearch || !ctx_r1.toolbar);
  }
}
const _c1 = a0 => ({
  "cds--search-close--hidden": a0
});
function Search_button_6_Template(rf, ctx) {
  if (rf & 1) {
    const _r13 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 12);
    i0.ɵɵlistener("click", function Search_button_6_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r13);
      const ctx_r12 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r12.clearSearch());
    });
    i0.ɵɵelementStart(1, "span", 13);
    i0.ɵɵtext(2);
    i0.ɵɵelementEnd();
    i0.ɵɵnamespaceSVG();
    i0.ɵɵelement(3, "svg", 14);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r3 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(3, _c1, !ctx_r3.value || ctx_r3.value.length === 0))("title", ctx_r3.clearButtonTitle);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate(ctx_r3.clearButtonTitle);
  }
}
const _c2 = (a0, a1, a2, a3, a4, a5, a6, a7, a8, a9, a10, a11) => ({
  "cds--search--sm": a0,
  "cds--search--md": a1,
  "cds--search--lg": a2,
  "cds--search--light": a3,
  "cds--skeleton": a4,
  "cds--search--expandable": a5,
  "cds--search--expanded": a6,
  "cds--toolbar-search": a7,
  "cds--toolbar-search--active": a8,
  "cds--toolbar-search-container-persistent": a9,
  "cds--toolbar-search-container-expandable": a10,
  "cds--toolbar-search-container-active": a11
});
class Search {
  /**
   * Creates an instance of `Search`.
   * @param i18n The i18n translations.
   */
  constructor(elementRef, i18n) {
    this.elementRef = elementRef;
    this.i18n = i18n;
    /**
     * @deprecated since v5 - Use `cdsLayer` directive instead
     * `light` or `dark` search theme.
     */
    this.theme = "dark";
    /**
     * Size of the search field.
     */
    this.size = "md";
    /**
     * Set to `true` for a disabled search input.
     */
    this.disabled = false;
    /**
     * Set to `true` for a toolbar search component.
     */
    this.toolbar = false;
    /**
     * Set to `true` to make the search component expandable.
     * `expandable` would override `toolbar` property behaviours.
     */
    this.expandable = false;
    /**
     * Set to `true` for a loading search component.
     */
    this.skeleton = false;
    /**
     * Set to `true` to expand the toolbar search component.
     */
    this.active = false;
    /**
     * Specifies whether the search component is used in the table toolbar.
     */
    this.tableSearch = false;
    /**
     * The unique id for the search component.
     */
    this.id = `search-${Search.searchCount}`;
    /**
     * Sets the value attribute on the `input` element.
     */
    this.value = "";
    /**
     * Sets the autocomplete attribute on the `input` element.
     * For reference: https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete#Values
     */
    this.autocomplete = "on";
    /**
     * Sets the text inside the `label` tag.
     */
    this.label = this.i18n.get().SEARCH.LABEL;
    /**
     * Sets the placeholder attribute on the `input` element.
     */
    this.placeholder = this.i18n.get().SEARCH.PLACEHOLDER;
    /**
     * Used to set the `title` attribute of the clear button.
     */
    this.clearButtonTitle = this.i18n.get().SEARCH.CLEAR_BUTTON;
    /**
     * Title for the search trigger
     */
    this.searchTitle = "";
    /**
     * Emits an event when value is changed.
     */
    this.valueChange = new EventEmitter();
    this.open = new EventEmitter();
    /**
     * Emits an event when the clear button is clicked.
     */
    this.clear = new EventEmitter();
    /**
     * Emits an event on enter.
     */
    this.search = new EventEmitter();
    /**
     * Sets `true` when composing text via IME.
     */
    this.isComposing = false;
    /**
     * Called when search input is blurred. Needed to properly implement `ControlValueAccessor`.
     */
    this.onTouched = () => {};
    /**
     * Method set in `registerOnChange` to propagate changes back to the form.
     */
    this.propagateChange = _ => {};
    Search.searchCount++;
  }
  get containerClass() {
    return !(this.toolbar || this.expandable);
  }
  /**
   * This is the initial value set to the component
   * @param value The input value.
   */
  writeValue(value) {
    this.value = value;
  }
  /**
   * Sets a method in order to propagate changes back to the form.
   */
  registerOnChange(fn) {
    this.propagateChange = fn;
  }
  /**
   * Registers a callback to be triggered when the control has been touched.
   * @param fn Callback to be triggered when the search input is touched.
   */
  registerOnTouched(fn) {
    this.onTouched = fn;
  }
  /**
   * Called when text is written in the input.
   * @param search The input text.
   */
  onSearch(search) {
    if (this.isComposing) {
      // check for IME use
      return;
    }
    this.value = search;
    this.doValueChange();
  }
  /**
   * Called on enter.
   */
  onEnter() {
    this.search.emit(this.value);
  }
  /**
   * Called when clear button is clicked.
   */
  clearSearch() {
    this.value = "";
    this.clear.emit();
    this.propagateChange(this.value);
  }
  doValueChange() {
    this.valueChange.emit(this.value);
    this.propagateChange(this.value);
  }
  openSearch() {
    this.active = true;
    this.open.emit(this.active);
    setTimeout(() => this.inputRef.nativeElement.focus());
  }
  keyDown(event) {
    if (this.toolbar || this.expandable) {
      if (event.key === "Escape") {
        this.active = false;
      } else if (event.key === "Enter") {
        this.openSearch();
      }
    }
  }
  focusOut(event) {
    this.onTouched();
    if ((this.expandable || this.toolbar) && this.inputRef && this.inputRef.nativeElement.value === "" && !this.elementRef.nativeElement.contains(event.relatedTarget)) {
      this.active = false;
      this.open.emit(this.active);
    }
  }
  focusIn(event) {
    this.onTouched();
    // set input focus if search icon get focus from tab key press event.
    if ((this.expandable || this.toolbar) && this.inputRef && !event.relatedTarget && !this.elementRef.nativeElement.contains(event.relatedTarget)) {
      this.openSearch();
    }
  }
  /**
   * Called when using IME composition.
   */
  compositionStart(event) {
    this.isComposing = true;
  }
  /**
   * Called when IME composition finishes.
   */
  compositionEnd(event) {
    this.isComposing = false;
    this.onSearch(this.value + event.data);
  }
}
/**
 * Variable used for creating unique ids for search components.
 */
Search.searchCount = 0;
Search.ɵfac = function Search_Factory(t) {
  return new (t || Search)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.I18n));
};
Search.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
  type: Search,
  selectors: [["cds-search"], ["ibm-search"]],
  viewQuery: function Search_Query(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵviewQuery(_c0, 5);
    }
    if (rf & 2) {
      let _t;
      i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.inputRef = _t.first);
    }
  },
  hostVars: 2,
  hostBindings: function Search_HostBindings(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵlistener("keydown", function Search_keydown_HostBindingHandler($event) {
        return ctx.keyDown($event);
      })("focusout", function Search_focusout_HostBindingHandler($event) {
        return ctx.focusOut($event);
      })("focusin", function Search_focusin_HostBindingHandler($event) {
        return ctx.focusIn($event);
      })("compositionstart", function Search_compositionstart_HostBindingHandler($event) {
        return ctx.compositionStart($event);
      })("compositionend", function Search_compositionend_HostBindingHandler($event) {
        return ctx.compositionEnd($event);
      });
    }
    if (rf & 2) {
      i0.ɵɵclassProp("cds--form-item", ctx.containerClass);
    }
  },
  inputs: {
    theme: "theme",
    size: "size",
    disabled: "disabled",
    toolbar: "toolbar",
    expandable: "expandable",
    skeleton: "skeleton",
    active: "active",
    tableSearch: "tableSearch",
    name: "name",
    id: "id",
    required: "required",
    value: "value",
    autocomplete: "autocomplete",
    label: "label",
    placeholder: "placeholder",
    clearButtonTitle: "clearButtonTitle",
    searchTitle: "searchTitle",
    ariaLabel: "ariaLabel"
  },
  outputs: {
    valueChange: "valueChange",
    open: "open",
    clear: "clear",
    search: "search"
  },
  features: [i0.ɵɵProvidersFeature([{
    provide: NG_VALUE_ACCESSOR,
    useExisting: Search,
    multi: true
  }])],
  decls: 7,
  vars: 20,
  consts: [["role", "search", 1, "cds--search", 3, "ngClass", "click"], [1, "cds--label", 3, "for"], ["class", "cds--search-input", 4, "ngIf", "ngIfElse"], ["enableInput", ""], ["class", "cds--search-close", 3, "ngClass", "title", "click", 4, "ngIf"], [1, "cds--search-input"], [1, "cds--search-input", 3, "type", "id", "value", "autocomplete", "placeholder", "disabled", "required", "input", "keyup.enter"], ["input", ""], ["class", "cds--toolbar-search__btn", "aria-label", "Open search", 3, "click", 4, "ngIf"], ["cdsIcon", "search", "class", "cds--search-magnifier-icon", "size", "16", 4, "ngIf"], ["aria-label", "Open search", 1, "cds--toolbar-search__btn", 3, "click"], ["cdsIcon", "search", "size", "16", 1, "cds--search-magnifier-icon"], [1, "cds--search-close", 3, "ngClass", "title", "click"], [1, "cds--visually-hidden"], ["cdsIcon", "close", "size", "16"]],
  template: function Search_Template(rf, ctx) {
    if (rf & 1) {
      i0.ɵɵelementStart(0, "div", 0);
      i0.ɵɵlistener("click", function Search_Template_div_click_0_listener() {
        return ctx.openSearch();
      });
      i0.ɵɵelementStart(1, "label", 1);
      i0.ɵɵtext(2);
      i0.ɵɵelementEnd();
      i0.ɵɵtemplate(3, Search_div_3_Template, 1, 0, "div", 2)(4, Search_ng_template_4_Template, 4, 9, "ng-template", null, 3, i0.ɵɵtemplateRefExtractor)(6, Search_button_6_Template, 4, 5, "button", 4);
      i0.ɵɵelementEnd();
    }
    if (rf & 2) {
      const _r2 = i0.ɵɵreference(5);
      i0.ɵɵproperty("ngClass", i0.ɵɵpureFunctionV(7, _c2, [ctx.size === "sm", ctx.size === "md", ctx.size === "lg", ctx.theme === "light", ctx.skeleton, ctx.expandable && !ctx.tableSearch, ctx.expandable && !ctx.tableSearch && ctx.active, ctx.toolbar && !ctx.expandable, ctx.toolbar && !ctx.expandable && ctx.active, ctx.tableSearch && !ctx.expandable, ctx.tableSearch && ctx.expandable, ctx.tableSearch && ctx.expandable && ctx.active]));
      i0.ɵɵattribute("aria-label", ctx.ariaLabel);
      i0.ɵɵadvance();
      i0.ɵɵproperty("for", ctx.id);
      i0.ɵɵadvance();
      i0.ɵɵtextInterpolate(ctx.label);
      i0.ɵɵadvance();
      i0.ɵɵproperty("ngIf", ctx.skeleton)("ngIfElse", _r2);
      i0.ɵɵadvance(3);
      i0.ɵɵproperty("ngIf", ctx.tableSearch || !ctx.toolbar);
    }
  },
  dependencies: [i2.NgClass, i2.NgIf, i3.IconDirective],
  encapsulation: 2
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(Search, [{
    type: Component,
    args: [{
      selector: "cds-search, ibm-search",
      providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: Search,
        multi: true
      }],
      template: "<div\n\tclass=\"cds--search\"\n\t[ngClass]=\"{\n\t\t'cds--search--sm': size === 'sm',\n\t\t'cds--search--md': size === 'md',\n\t\t'cds--search--lg': size === 'lg',\n\t\t'cds--search--light': theme === 'light',\n\t\t'cds--skeleton': skeleton,\n\t\t'cds--search--expandable': expandable && !tableSearch,\n\t\t'cds--search--expanded': expandable && !tableSearch && active,\n\t\t'cds--toolbar-search': toolbar && !expandable,\n\t\t'cds--toolbar-search--active': toolbar && !expandable && active,\n\t\t'cds--toolbar-search-container-persistent': tableSearch && !expandable,\n\t\t'cds--toolbar-search-container-expandable': tableSearch && expandable,\n\t\t'cds--toolbar-search-container-active': tableSearch && expandable && active\n\t}\"\n\trole=\"search\"\n\t[attr.aria-label]=\"ariaLabel\"\n\t(click)=\"openSearch()\">\n\t<label class=\"cds--label\" [for]=\"id\">{{label}}</label>\n\n\t<div *ngIf=\"skeleton; else enableInput\" class=\"cds--search-input\"></div>\n\t<ng-template #enableInput>\n\t\t<input\n\t\t\t#input\n\t\t\tclass=\"cds--search-input\"\n\t\t\t[type]=\"tableSearch || !toolbar ? 'text' : 'search'\"\n\t\t\t[id]=\"id\"\n\t\t\t[value]=\"value\"\n\t\t\t[autocomplete]=\"autocomplete\"\n\t\t\t[placeholder]=\"placeholder\"\n\t\t\t[disabled]=\"disabled\"\n\t\t\t[required]=\"required\"\n\t\t\t(input)=\"onSearch($event.target.value)\"\n\t\t\t(keyup.enter)=\"onEnter()\"/>\n\t\t<button\n\t\t\t*ngIf=\"!tableSearch && toolbar\"\n\t\t\tclass=\"cds--toolbar-search__btn\"\n\t\t\t(click)=\"openSearch()\"\n\t\t\taria-label=\"Open search\">\n\t\t\t<svg cdsIcon=\"search\" size=\"16\" class=\"cds--search-magnifier-icon\"></svg>\n\t\t</button>\n\t\t<svg\n\t\t\tcdsIcon=\"search\"\n\t\t\t*ngIf=\"tableSearch || !toolbar\"\n\t\t\tclass=\"cds--search-magnifier-icon\"\n\t\t\tsize=\"16\">\n\t\t</svg>\n\t</ng-template>\n\n\t<button\n\t\t*ngIf=\"tableSearch || !toolbar\"\n\t\tclass=\"cds--search-close\"\n\t\t[ngClass]=\"{\n\t\t\t'cds--search-close--hidden': !value || value.length === 0\n\t\t}\"\n\t\t[title]=\"clearButtonTitle\"\n\t\t(click)=\"clearSearch()\">\n\t\t<span class=\"cds--visually-hidden\">{{ clearButtonTitle }}</span>\n\t\t<svg cdsIcon=\"close\" size=\"16\"></svg>\n\t</button>\n</div>\n"
    }]
  }], function () {
    return [{
      type: i0.ElementRef
    }, {
      type: i1.I18n
    }];
  }, {
    containerClass: [{
      type: HostBinding,
      args: ["class.cds--form-item"]
    }],
    theme: [{
      type: Input
    }],
    size: [{
      type: Input
    }],
    disabled: [{
      type: Input
    }],
    toolbar: [{
      type: Input
    }],
    expandable: [{
      type: Input
    }],
    skeleton: [{
      type: Input
    }],
    active: [{
      type: Input
    }],
    tableSearch: [{
      type: Input
    }],
    name: [{
      type: Input
    }],
    id: [{
      type: Input
    }],
    required: [{
      type: Input
    }],
    value: [{
      type: Input
    }],
    autocomplete: [{
      type: Input
    }],
    label: [{
      type: Input
    }],
    placeholder: [{
      type: Input
    }],
    clearButtonTitle: [{
      type: Input
    }],
    searchTitle: [{
      type: Input
    }],
    ariaLabel: [{
      type: Input
    }],
    valueChange: [{
      type: Output
    }],
    open: [{
      type: Output
    }],
    clear: [{
      type: Output
    }],
    search: [{
      type: Output
    }],
    inputRef: [{
      type: ViewChild,
      args: ["input"]
    }],
    keyDown: [{
      type: HostListener,
      args: ["keydown", ["$event"]]
    }],
    focusOut: [{
      type: HostListener,
      args: ["focusout", ["$event"]]
    }],
    focusIn: [{
      type: HostListener,
      args: ["focusin", ["$event"]]
    }],
    compositionStart: [{
      type: HostListener,
      args: ["compositionstart", ["$event"]]
    }],
    compositionEnd: [{
      type: HostListener,
      args: ["compositionend", ["$event"]]
    }]
  });
})();

// modules
class SearchModule {}
SearchModule.ɵfac = function SearchModule_Factory(t) {
  return new (t || SearchModule)();
};
SearchModule.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
  type: SearchModule
});
SearchModule.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
  imports: [FormsModule, CommonModule, I18nModule, IconModule]
});
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SearchModule, [{
    type: NgModule,
    args: [{
      declarations: [Search],
      exports: [Search],
      imports: [FormsModule, CommonModule, I18nModule, IconModule]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { Search, SearchModule };
